/**
 * Personal Teamwork API key.
 *
 * Support tickets filed in Core are mirrored to Teamwork tasks. Teamwork
 * credits the task to whoever owns the API key used to create it, so without a
 * personal key every ticket you raise shows a shared service account as the
 * creator. Setting a key here makes your tickets yours in Teamwork.
 *
 * Optional by design — leaving it empty keeps today's behaviour (the shared
 * account files the task), and clients have no Teamwork account at all.
 */
import { useState } from 'react';
import { KeyRound, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { userIntegrationsService } from '@/services/userIntegrationsService';

const QUERY_KEY = ['account', 'integrations', 'teamwork'];

export function TeamworkIntegrationCard() {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');

  const { data: status, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => userIntegrationsService.getTeamwork(),
  });

  const save = useMutation({
    mutationFn: () => userIntegrationsService.setTeamwork(apiKey.trim()),
    onSuccess: (res) => {
      setApiKey('');
      queryClient.setQueryData(QUERY_KEY, res);
      const who = res.metadata?.teamwork_name || res.metadata?.teamwork_email;
      toast.success(
        who
          ? `Teamwork connected as ${who}. New tickets will be filed as you.`
          : 'Teamwork connected. New tickets will be filed as you.',
      );
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || 'Could not save that key. Please try again.';
      toast.error(detail);
    },
  });

  const remove = useMutation({
    mutationFn: () => userIntegrationsService.removeTeamwork(),
    onSuccess: (res) => {
      queryClient.setQueryData(QUERY_KEY, res);
      toast.success(
        'Teamwork key removed. Your tickets will be filed by the shared account.',
      );
    },
    onError: () => toast.error('Could not remove the key. Please try again.'),
  });

  const connected = Boolean(status?.configured);
  // Clients have no Teamwork account, so there is nothing here for them to do.
  // Their tickets are filed under the External Core Support account, which is
  // correct rather than degraded — showing this card would invite them to
  // solve a problem they do not have.
  const internal = status?.internal ?? false;
  const who = status?.metadata?.teamwork_name || status?.metadata?.teamwork_email;

  if (!isLoading && !internal) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-coral" />
          Teamwork
        </CardTitle>
        <CardDescription>
          Support tickets you file are mirrored to Teamwork. Add your own API key
          so those tasks show you as the creator instead of a shared account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Checking…</p>
        ) : connected ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-teal-dark" />
              <span>
                Connected{who ? ` as ${who}` : ''}. New tickets are filed as you.
              </span>
            </div>
            <Button
              variant="outline"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Not connected — your tickets are currently filed in Teamwork under
            the shared External Core Support account rather than your name.
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="teamwork-api-key">
            {connected ? 'Replace your API key' : 'Your Teamwork API key'}
          </Label>
          <div className="flex gap-2">
            <Input
              id="teamwork-api-key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Paste your personal API key"
            />
            <Button
              onClick={() => save.mutate()}
              disabled={apiKey.trim().length < 8 || save.isPending}
            >
              {save.isPending ? 'Checking…' : 'Save'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Find it in Teamwork under your profile → Edit My Details → API &amp;
            Mobile. We check the key with Teamwork before saving it, and store it
            encrypted. It is never shown again.{' '}
            <a
              href="https://teamwork.agileandco.com/desk/api-keys"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 underline"
            >
              Open Teamwork
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
